import React from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

/**
 * Props for OpenSnowsightButton component
 */
interface OpenSnowsightButtonProps {
  /** Snowflake account identifier (e.g., "myorg-myaccount") */
  accountIdentifier: string;
  /** Optional custom button text */
  buttonText?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Optional CSS classes */
  className?: string;
}

/**
 * OpenSnowsightButton Component
 *
 * Button that opens a new tab to the Snowflake Snowsight UI for the specified
 * account identifier.
 *
 * @component
 * @example
 * ```tsx
 * <OpenSnowsightButton
 *   accountIdentifier="myorg-myaccount"
 *   buttonText="Open Snowflake Console"
 * />
 * ```
 */
export const OpenSnowsightButton: React.FC<OpenSnowsightButtonProps> = ({
  accountIdentifier,
  buttonText = 'Open Snowsight',
  disabled = false,
  className = '',
}) => {
  /**
   * Opens Snowsight in a new tab
   */
  const handleOpenSnowsight = (): void => {
    if (accountIdentifier.trim() === '') {
      console.warn('Cannot open Snowsight: account identifier is empty');
      return;
    }

    const snowsightUrl = `https://${accountIdentifier}.snowflakecomputing.com`;
    window.open(snowsightUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.button
      type="button"
      onClick={handleOpenSnowsight}
      disabled={disabled || accountIdentifier.trim() === ''}
      className={`inline-flex items-center justify-center gap-2 px-4 py-1 border border-dataops-primary-blue text-sm rounded-md text-dataops-dark bg-white hover:bg-dataops-primary-blue/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 ${className}`}
      whileHover={!disabled && accountIdentifier.trim() !== '' ? { scale: 1.02 } : {}}
      whileTap={!disabled && accountIdentifier.trim() !== '' ? { scale: 0.98 } : {}}
      aria-label={`Open Snowsight console for ${accountIdentifier}`}
    >
      <span>{buttonText}</span>
      <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
    </motion.button>
  );
};
